import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MdClose, MdPause, MdPlayArrow, MdVideocam } from 'react-icons/md';

/**
 * Attachment tray for the composer: what you have queued, above the input.
 *
 * Shared by the direct-message pane, the group pane and the thread panel. Each
 * of those had its own copy of this markup, and all four drifted: a strip
 * pinned with `absolute` but no `left`/`right`, so it started at its static
 * position and ran past the composer's right edge (which is what clipped the
 * close button in the narrow thread column); a 24px top radius against the
 * composer's 16px; and `border-[${Colors.borderColor}]`, which interpolates to
 * `border-[var(--border)]` at runtime — a class Tailwind never generated, so
 * the border it was asking for was simply absent.
 */

const pad = (n) => String(n).padStart(2, '0');

const formatClock = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '--:--';
  const whole = Math.floor(seconds);
  return `${pad(Math.floor(whole / 60))}:${pad(whole % 60)}`;
};

/**
 * Object URLs for a list of files, created once per list rather than per
 * render. The call sites built them inline in JSX, so every keystroke in the
 * composer minted a fresh blob URL per attachment and leaked the previous one
 * for the lifetime of the document.
 */
const useObjectUrls = (files) => {
  const urls = useMemo(() => (files || []).map((file) => URL.createObjectURL(file)), [files]);

  useEffect(() => () => urls.forEach((url) => URL.revokeObjectURL(url)), [urls]);

  return urls;
};

const useObjectUrl = (file) => {
  const url = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);

  useEffect(() => {
    if (!url) return undefined;
    return () => URL.revokeObjectURL(url);
  }, [url]);

  return url;
};

/**
 * Voice note preview.
 *
 * Hand-rolled rather than `<audio controls>`: the native player brings its own
 * opaque pill, volume slider and overflow menu, none of which follow the theme,
 * and at the width of the thread column it pushed its own controls out of view.
 * This is the one control the preview actually needs — play, scrub, position.
 */
const VoiceNotePreview = ({ src, fallbackDuration }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [length, setLength] = useState(null);

  // MediaRecorder blobs report `duration: Infinity` until they are seeked, so
  // the recorded elapsed time is the reliable source when metadata has none.
  const total = Number.isFinite(length) && length > 0 ? length : fallbackDuration;
  const progress = total > 0 ? Math.min(100, (position / total) * 100) : 0;

  useEffect(() => {
    setPlaying(false);
    setPosition(0);
    setLength(null);
  }, [src]);

  const toggle = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      el.play().catch(() => setPlaying(false));
    } else {
      el.pause();
    }
  }, []);

  const seek = useCallback(
    (event) => {
      const el = audioRef.current;
      if (!el || !(total > 0)) return;
      const { left, width } = event.currentTarget.getBoundingClientRect();
      if (!width) return;
      const ratio = Math.min(1, Math.max(0, (event.clientX - left) / width));
      el.currentTime = ratio * total;
      setPosition(ratio * total);
    },
    [total]
  );

  return (
    <div className="voice-preview">
      <button
        type="button"
        className="voice-preview-play"
        title={playing ? 'Pause' : 'Play'}
        aria-label={playing ? 'Pause voice note' : 'Play voice note'}
        onClick={toggle}
      >
        {playing ? <MdPause size={18} /> : <MdPlayArrow size={20} />}
      </button>

      <div
        className="voice-preview-track"
        role="presentation"
        title="Seek"
        onClick={seek}
        onKeyDown={undefined}
      >
        <span className="voice-preview-fill" style={{ width: `${progress}%` }} />
      </div>

      <span className="voice-preview-time">
        {formatClock(position)} / {formatClock(total)}
      </span>

      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setPosition(0);
        }}
        onTimeUpdate={(e) => setPosition(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setLength(e.currentTarget.duration)}
      />
    </div>
  );
};

const ComposerAttachments = ({
  images = [],
  onRemoveImage,
  onClearImages,
  audio = null,
  onRemoveAudio,
  recordedSeconds,
  busy = false,
}) => {
  const imageUrls = useObjectUrls(images);
  const audioUrl = useObjectUrl(audio);

  const hasAudio = Boolean(audio);
  const hasImages = images.length > 0;

  if (!hasAudio && !hasImages) return null;

  const label = hasAudio
    ? 'Voice note'
    : `${images.length} ${images.length === 1 ? 'attachment' : 'attachments'}`;

  const clear = hasAudio ? onRemoveAudio : onClearImages;

  return (
    <div className="composer-tray" role="group" aria-label="Pending attachments">
      <div className="composer-tray-head">
        <span className="composer-tray-label">{label}</span>
        {busy ? (
          <span className="composer-tray-busy">
            <span className="btn-spinner" />
            Uploading…
          </span>
        ) : (
          clear && (
            <button
              type="button"
              className="composer-tray-clear"
              title="Discard"
              aria-label="Discard attachments"
              onClick={() => clear()}
            >
              <MdClose size={15} />
            </button>
          )
        )}
      </div>

      {hasAudio ? (
        <VoiceNotePreview src={audioUrl} fallbackDuration={recordedSeconds} />
      ) : (
        <div className="composer-tray-media custom-scrollbar">
          {images.map((file, index) => {
            const isVideo = file?.type?.startsWith('video/');

            return (
              <span
                className="composer-thumb"
                key={`${file?.name || 'file'}-${file?.lastModified || index}`}
              >
                {isVideo ? (
                  <video src={imageUrls[index]} muted playsInline />
                ) : (
                  <img src={imageUrls[index]} alt={file?.name || `Attachment ${index + 1}`} />
                )}

                {isVideo && (
                  <span className="composer-thumb-kind" aria-hidden="true">
                    <MdVideocam size={13} />
                  </span>
                )}

                {!busy && (
                  <button
                    type="button"
                    className="composer-thumb-remove"
                    title="Remove"
                    aria-label={`Remove ${file?.name || `attachment ${index + 1}`}`}
                    onClick={() => onRemoveImage?.(index)}
                  >
                    <MdClose size={12} />
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ComposerAttachments;
